/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewEncapsulation, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { DateInputComponent, PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { DmsMetadataService, DmsOption } from '../upload/dms-metadata.service';
import { OcrHit, OcrSearchService } from './ocr-search.service';
import { DocumentSearchService, EMPTY_CRITERIA, SavedSearch, SearchCriteria, SearchHit, SearchSort } from './document-search.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const PAGE_SIZE = 20;

const SORTS: { value: SearchSort; label: string }[] = [
  { value: 'cm:modified', label: 'DMS.UI.69C580' },
  { value: 'cm:created', label: 'DMS.UI.1ECED3' },
  { value: 'cm:name', label: 'DMS.UI.F882A4' },
  { value: 'score', label: 'DMS.UI.C8001E' }
];

/** File-size bands offered by the size filter, in bytes. */
const SIZES: { label: string; min: number; max: number }[] = [
  { label: 'DMS.UI.3D1FC4', min: 0, max: 0 },
  { label: 'DMS.UI.DA99E2', min: 0, max: 1024 * 1024 },
  { label: '1 – 10 MB', min: 1024 * 1024, max: 10 * 1024 * 1024 },
  { label: '10 – 100 MB', min: 10 * 1024 * 1024, max: 100 * 1024 * 1024 },
  { label: 'DMS.UI.0C96B7', min: 100 * 1024 * 1024, max: 0 }
];

const BADGES: Record<string, string> = {
  pdf: 'PDF',
  doc: 'W',
  docx: 'W',
  xls: 'X',
  xlsx: 'X',
  ppt: 'P',
  pptx: 'P',
  txt: 'TXT',
  jpg: 'IMG',
  jpeg: 'IMG',
  png: 'IMG'
};

/** A run of a found page's text, and whether it is the searched word. */
export interface FragmentPart {
  text: string;
  hit: boolean;
}

const ENTITIES: Record<string, string> = { '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'", '&#x2F;': '/', '&amp;': '&' };

/**
 * The search index's highlight — the page's text with the searched words in
 * <em> — as plain text runs. The text came out of a scanned or uploaded file,
 * so it is shown as text and never parsed as markup: only the <em> markers
 * are read, and the entities the index escaped are turned back into text.
 */
export function fragmentParts(fragment: string): FragmentPart[] {
  return (fragment ?? '')
    .split(/(<em>[\s\S]*?<\/em>)/)
    .filter((part) => part.length > 0)
    .map((part) => {
      const hit = part.startsWith('<em>') && part.endsWith('</em>');
      const text = (hit ? part.slice(4, -5) : part).replace(/&(?:lt|gt|quot|#39|#x27|#x2F|amp);/g, (e) => ENTITIES[e]);
      return { text, hit };
    });
}

/**
 * Advanced document search (UI-07).
 *
 * Every filter maps to a real indexed field; the query is assembled by
 * {@link DocumentSearchService} and executed server-side. Saved searches are
 * stored as a server-side user preference, so they follow the user across
 * devices.
 *
 * The screen offers only filters this repository can actually answer. The
 * reference also shows "ภาษา" and "มีไฟล์แนบเท่านั้น"; neither corresponds to
 * anything in the deployed content model, and a control that silently does
 * nothing is worse than one that is absent.
 */
@Component({
  selector: 'app-document-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    DateInputComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './document-search.component.html',
  styleUrls: ['./document-search.component.scss']
})
export class DocumentSearchComponent implements OnInit {
  readonly fragmentParts = fragmentParts;
  private readonly translate = inject(TranslateService);
  private readonly search = inject(DocumentSearchService);
  private readonly ocrSearch = inject(OcrSearchService);
  private readonly dms = inject(DmsMetadataService);
  private readonly notification = inject(NotificationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly sorts = SORTS;
  readonly sizes = SIZES;

  readonly hits = signal<SearchHit[]>([]);

  /**
   * What the words read off the scanned pages turned up, kept apart from the
   * repository's own answer. The two searches finish on their own timing, and
   * merging them as they land meant whichever came last overwrote the other —
   * so they are held separately and put together only when the list is drawn.
   */
  readonly ocrHits = signal<OcrHit[]>([]);

  /**
   * How many documents carry the words but may not be read by this person. The
   * repository decides that; the number is shown so the list does not look
   * quietly incomplete.
   */
  readonly ocrLoading = signal(false);

  /**
   * The one list the screen shows: everything the repository found, plus the
   * documents only the scanned words knew about. A document both found appears
   * once, keeping its place and gaining the page the words were on.
   */
  readonly rows = computed<SearchHit[]>(() => {
    const merged = [...this.hits()];
    const scans = this.ocrHits();
    if (!scans.length) {
      return merged;
    }

    const seen = new Map(merged.map((hit, index) => [hit.id, index]));
    for (const found of this.byDocument(scans)) {
      const scanned = { ocrPage: found.best.page, ocrPages: found.pages, ocrFragments: found.best.fragments };
      const at = seen.get(found.best.nodeId);
      if (at !== undefined) {
        merged[at] = { ...merged[at], ...scanned };
        continue;
      }
      merged.push({ ...this.fromScan(found.best), ...scanned });
    }

    return this.reorder(merged);
  });

  /** What the count above the list should say, once the scans are counted in. */
  readonly rowTotal = computed<number | null>(() => {
    const repository = this.total();
    const extra = this.rows().length - this.hits().length;
    if (!extra) {
      return repository;
    }
    return (repository ?? this.hits().length) + extra;
  });
  readonly total = signal<number | null>(null);
  readonly hasMore = signal(false);
  readonly loading = signal(false);
  readonly searched = signal(false);
  readonly failed = signal('');
  readonly filtersOpen = signal(true);
  readonly view = signal<'list' | 'grid'>('list');
  readonly savedSearches = signal<SavedSearch[]>([]);

  documentTypes: DmsOption[] = [];
  statuses: DmsOption[] = [];

  criteria: SearchCriteria = { ...EMPTY_CRITERIA };
  sort: SearchSort = 'cm:modified';
  sizeIndex = 0;
  pageIndex = 0;

  ngOnInit(): void {
    this.dms.schema().subscribe((schema) => {
      this.documentTypes = schema.fields['dms:documentType']?.options ?? [];
      this.statuses = schema.fields['dms:status']?.options ?? [];
    });

    this.search.saved().subscribe((list) => this.savedSearches.set(list));

    // the global header search lands here with ?q= — read it live, not from a
    // snapshot: submitting again while this page is already open changes only
    // the params, which a snapshot never notices, so the new words went into
    // the address bar and the search did not run
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const q = params.get('q');
      if (q && q !== this.criteria.term) {
        this.criteria.term = q;
        // words typed in the header want results, not the filter form: the
        // filters fold away so the hits start at the top of the page
        this.filtersOpen.set(false);
        this.run();
      }
    });
  }

  // ---- searching ----------------------------------------------------------

  run(): void {
    this.pageIndex = 0;
    this.syncQueryParam();
    this.execute();
  }

  /**
   * Keeps `?q=` mirroring the term, with `replaceUrl` so in-page searches do
   * not pile onto the history stack. Without it the param went stale the
   * moment this page's own box was used — and a header re-submit of the words
   * still in the URL then navigated to what was already current: no event,
   * no search.
   */
  private syncQueryParam(): void {
    const q = this.criteria.term?.trim() ?? '';
    if (q !== (this.route.snapshot.queryParamMap.get('q') ?? '')) {
      void this.router.navigate([], { relativeTo: this.route, queryParams: { q: q || null }, replaceUrl: true });
    }
  }

  private execute(): void {
    this.searchScans();
    this.loading.set(true);
    this.failed.set('');
    this.searched.set(true);

    this.search.search(this.criteria, this.sort, this.pageIndex * PAGE_SIZE, PAGE_SIZE).subscribe({
      next: (results) => {
        this.hits.set(results.hits);
        this.total.set(results.total);
        this.hasMore.set(results.hasMoreItems);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.hits.set([]);
        this.failed.set(this.describe(err, 'DMS.UI.BC81CE'));
      }
    });
  }

  clear(): void {
    this.criteria = { ...EMPTY_CRITERIA };
    this.sizeIndex = 0;
    this.hits.set([]);
    this.ocrHits.set([]);
    this.total.set(null);
    this.searched.set(false);
    this.syncQueryParam();
  }

  toggleFilters(): void {
    this.filtersOpen.update((open) => !open);
  }

  changeSort(sort: SearchSort): void {
    this.sort = sort;
    this.pageIndex = 0;
    this.execute();
  }

  setView(view: 'list' | 'grid'): void {
    this.view.set(view);
  }

  changeSize(index: number): void {
    this.sizeIndex = Number(index);
    const band = SIZES[this.sizeIndex] ?? SIZES[0];
    this.criteria.sizeMin = band.min;
    this.criteria.sizeMax = band.max;
  }

  nextPage(): void {
    if (!this.hasMore()) {
      return;
    }
    this.pageIndex += 1;
    this.execute();
  }

  previousPage(): void {
    if (this.pageIndex === 0) {
      return;
    }
    this.pageIndex -= 1;
    this.execute();
  }

  get rangeStart(): number {
    return this.rows().length ? this.pageIndex * PAGE_SIZE + 1 : 0;
  }

  get rangeEnd(): number {
    return this.pageIndex * PAGE_SIZE + this.rows().length;
  }

  get queryPreview(): string {
    return this.search.preview(this.criteria);
  }

  get hasCriteria(): boolean {
    return !this.search.isEmpty(this.criteria);
  }

  // ---- saved searches -----------------------------------------------------

  saveCurrent(): void {
    if (!this.hasCriteria) {
      this.notification.showWarning(this.translate.instant('DMS.UI.A59DB6'));
      return;
    }

    const name = this.describeCriteria().slice(0, 60);
    this.search.save(name, { ...this.criteria }).subscribe({
      next: (list) => {
        this.savedSearches.set(list);
        this.notification.showInfo(this.translate.instant('DMS.UI.A09866', { p0: name }));
      },
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.735CE1'))
    });
  }

  /**
   * A readable name for the current criteria, so a filter-only search is not
   * saved under a generic label the user cannot tell apart from another.
   */
  private describeCriteria(): string {
    const parts: string[] = [];
    const c = this.criteria;

    if (c.term.trim()) {
      parts.push(c.term.trim());
    }
    if (c.contentText.trim()) {
      parts.push(this.translate.instant('DMS.UI.A10EA0', { p0: c.contentText.trim() }));
    }
    if (c.documentType) {
      parts.push(this.translate.instant(this.label(c.documentType)));
    }
    if (c.status) {
      parts.push(this.translate.instant('DMS.UI.B6F265', { p0: this.translate.instant(this.label(c.status)) }));
    }
    if (c.department.trim()) {
      parts.push(c.department.trim());
    }
    if (c.owner.trim()) {
      parts.push(this.translate.instant('DMS.UI.296076', { p0: c.owner.trim() }));
    }
    if (c.tag.trim()) {
      parts.push(this.translate.instant('DMS.UI.D5F97A', { p0: c.tag.trim() }));
    }
    if (c.createdFrom || c.createdTo) {
      parts.push(this.translate.instant('DMS.UI.96E8F8'));
    }
    if (c.modifiedFrom || c.modifiedTo) {
      parts.push(this.translate.instant('DMS.UI.95E475'));
    }
    if (c.sizeMin || c.sizeMax) {
      parts.push(this.translate.instant(SIZES[this.sizeIndex]?.label ?? 'DMS.UI.87B066'));
    }

    return parts.join(' · ') || this.translate.instant('DMS.UI.AA7E40');
  }

  applySaved(entry: SavedSearch): void {
    this.criteria = { ...EMPTY_CRITERIA, ...entry.criteria };
    this.sizeIndex = SIZES.findIndex((band) => band.min === this.criteria.sizeMin && band.max === this.criteria.sizeMax);
    if (this.sizeIndex < 0) {
      this.sizeIndex = 0;
    }
    this.run();
  }

  removeSaved(entry: SavedSearch, event: Event): void {
    event.stopPropagation();
    this.search.remove(entry.name).subscribe({
      next: (list) => this.savedSearches.set(list),
      error: (err) => this.notification.showError(this.describe(err, 'DMS.UI.60067C'))
    });
  }

  // ---- navigation ---------------------------------------------------------

  open(hit: SearchHit): void {
    void this.router.navigate(hit.isFolder ? ['/personal-files', hit.id] : ['/document', hit.id]);
  }

  // ---- view helpers -------------------------------------------------------

  label(value: string): string {
    return value ? DmsMetadataService.label(value) : '';
  }

  badge(hit: SearchHit): string {
    return hit.isFolder ? '' : (BADGES[this.extension(hit.name)] ?? 'FILE');
  }

  badgeClass(hit: SearchHit): string {
    return 'aca-search__badge--' + (hit.isFolder ? 'folder' : this.extension(hit.name));
  }

  initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || (name?.[0] ?? '?').toUpperCase();
  }

  formatSize(bytes?: number): string {
    if (bytes === undefined || bytes === null) {
      return '';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  }

  trackById(_: number, hit: SearchHit): string {
    return hit.id;
  }

  trackByName(_: number, entry: SavedSearch): string {
    return entry.name;
  }

  private extension(name: string): string {
    return name.split('.').pop()?.toLowerCase() ?? '';
  }

  /**
   * Asks the OCR index the same question the repository was asked. Its answer is
   * only recorded here; `rows` is what puts the two together.
   */
  private searchScans(): void {
    const term = (this.criteria.contentText || this.criteria.term || '').trim();
    this.ocrHits.set([]);
    if (!term) {
      return;
    }

    this.ocrLoading.set(true);
    // pages, not documents: one long document can match on many, so ask for enough that others still show
    this.ocrSearch.search(term, 60).subscribe((result) => {
      this.ocrLoading.set(false);
      this.ocrHits.set(result?.hits ?? []);
    });
  }

  /**
   * The OCR index answers page by page, so a long document that carries the
   * words on many pages came back as many hits — and was listed once per page.
   * Gathered here into one entry per document: its best page leads (the index
   * returns the best match first), and every page it was found on goes with it.
   */
  private byDocument(scans: OcrHit[]): { best: OcrHit; pages: number[] }[] {
    const documents = new Map<string, { best: OcrHit; pages: number[] }>();
    for (const scan of scans) {
      const found = documents.get(scan.nodeId);
      if (!found) {
        documents.set(scan.nodeId, { best: scan, pages: [scan.page] });
      } else if (!found.pages.includes(scan.page)) {
        found.pages.push(scan.page);
      }
    }
    return [...documents.values()].map((found) => ({ ...found, pages: [...found.pages].sort((a, b) => a - b) }));
  }

  /** The pages a document was found on — the first ten, when there are more. */
  pageList(pages: number[] | undefined): string {
    const list = pages ?? [];
    return list.length > 10 ? list.slice(0, 10).join(', ') + ' …' : list.join(', ');
  }

  /** A row for a document only the scanned words knew about. */
  private fromScan(scan: OcrHit): SearchHit {
    return {
      id: scan.nodeId,
      name: scan.name,
      title: scan.title ?? '',
      isFolder: false,
      location: scan.path ?? '',
      description: '',
      modifiedAt: scan.modifiedAt ?? '',
      modifiedByName: scan.modifiedBy ?? '',
      sizeInBytes: scan.sizeInBytes ?? undefined,
      documentType: '',
      status: '',
      documentCode: '',
      tags: [],
      ocrPage: scan.page,
      ocrFragments: scan.fragments
    };
  }

  /**
   * Puts the merged list back in the order the reader asked for.
   *
   * The two sources hand dates over differently — the repository's client parses
   * them into `Date`, the sidecar answers with the ISO string — so both are put
   * through `when()` rather than compared as text.
   */
  private reorder(hits: SearchHit[]): SearchHit[] {
    const merged = [...hits];
    if (this.sort === 'cm:name') {
      return merged.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'th'));
    }
    if (this.sort === 'cm:modified' || this.sort === 'cm:created') {
      return merged.sort((a, b) => this.when(b.modifiedAt) - this.when(a.modifiedAt));
    }
    // "most relevant": the repository's own ranking first, the scanned ones after
    return merged;
  }

  /** A date as a number, whatever shape it arrived in. */
  private when(value: unknown): number {
    if (!value) {
      return 0;
    }
    const parsed = value instanceof Date ? value.getTime() : Date.parse(String(value));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private describe(error: any, fallback: string): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant(fallback);
  }
}
